import React, { FC } from 'react';
import pluralize from 'pluralize';
import { EventDTO } from '@atlassiansox/phobos-client-ts';

import {
  BREACH_COLOR,
  BURN_COLOR,
  flex,
  flexDirectionColumn,
  spaceGapMinimum,
  BREACH_LIGHT_COLOR,
  BURN_LIGHT_COLOR,
  INVERSE_COLOR,
  spaceTopComfortable,
  HISTORICAL_ALERT_COLOR,
  HISTORICAL_LIGHT_ALERT_COLOR,
  CTD_ERROR_RATE_COLOR,
  LOW_TRAFFIC_ALERT_COLOR,
} from '@/styles';
import { GraphEventSource } from '@/components/composed/new-slo-views/expanded/graph/graph-tabs';

import { getEventTypes } from '../../utils';

import EventsBadge from './events-badge';

type EventsListProps = {
  events: EventDTO[];
  graphEventSource?: GraphEventSource;
};

type EventsListItem = [Array<EventDTO>, boolean, string, string, string, boolean, boolean];

const EventsList: FC<EventsListProps> = ({ events: allEvents, graphEventSource }) => {
  if (allEvents.length === 0) {
    return <></>;
  }

  const {
    breachEvents,
    burnEvents,
    sfxBreachEvents,
    sfxBurnEvents,
    lowTrafficBreachEvents,
    lowTrafficBurnEvents,
    lowTrafficSFxBreachEvents,
    lowTrafficSFxBurnEvents,
    sfxCtdErrorRateEvents,
    sfxLowTrafficAlertEvents,
  } = getEventTypes(allEvents, graphEventSource);

  const showHistoricalEvents = graphEventSource === GraphEventSource.Historical;

  const EventNodes: EventsListItem[] = [
    [
      breachEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR,
      INVERSE_COLOR,
      `SLO ${pluralize('Breach', breachEvents.length)}`,
      false,
      false,
    ],
    [
      lowTrafficBreachEvents,
      false,
      showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BREACH_LIGHT_COLOR,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR,
      `SLO ${pluralize('Breach', lowTrafficBreachEvents.length)}`,
      false,
      true,
    ],
    [
      burnEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR,
      INVERSE_COLOR,
      `Error Budget ${pluralize('Burn', burnEvents.length)}`,
      false,
      false,
    ],
    [
      lowTrafficBurnEvents,
      false,
      showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BURN_LIGHT_COLOR,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR,
      `Error Budget ${pluralize('Burn', lowTrafficBurnEvents.length)}`,
      false,
      true,
    ],
    [
      sfxBreachEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR,
      INVERSE_COLOR,
      `Breach ${pluralize('Alert', sfxBreachEvents.length)}`,
      false,
      false,
    ],
    [
      lowTrafficSFxBreachEvents,
      false,
      showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BREACH_LIGHT_COLOR,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR,
      `Breach ${pluralize('Alert', lowTrafficSFxBreachEvents.length)}`,
      false,
      true,
    ],
    [
      sfxBurnEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR,
      INVERSE_COLOR,
      `Burn ${pluralize('Alert', sfxBurnEvents.length)}`,
      false,
      false,
    ],
    [
      lowTrafficSFxBurnEvents,
      false,
      showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BURN_LIGHT_COLOR,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR,
      `Burn ${pluralize('Alert', lowTrafficSFxBurnEvents.length)}`,
      false,
      true,
    ],
    [
      sfxCtdErrorRateEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : CTD_ERROR_RATE_COLOR,
      INVERSE_COLOR,
      `Consecutive Trial Detector ${pluralize('Alert', sfxCtdErrorRateEvents.length)}`,
      false,
      false,
    ],
    [
      sfxLowTrafficAlertEvents,
      true,
      showHistoricalEvents ? HISTORICAL_ALERT_COLOR : LOW_TRAFFIC_ALERT_COLOR,
      INVERSE_COLOR,
      `Low Traffic ${pluralize('Alert', sfxLowTrafficAlertEvents.length)}`,
      false,
      false,
    ],
  ];

  return (
    <div css={[spaceGapMinimum, flex, flexDirectionColumn, spaceTopComfortable]}>
      {EventNodes.filter(([events]) => !!events.length).map(
        ([events, isBold, bgColor, textColor, caption, useWithSfxIcon, useLowTrafficTag]) => (
          <EventsBadge
            key={events[0].publicId}
            count={events.length}
            backgroundColor={bgColor}
            textColor={textColor}
            caption={caption}
            useWithSfxIcon={useWithSfxIcon}
            useLowTrafficTag={useLowTrafficTag}
            isBold={isBold}
          />
        )
      )}
    </div>
  );
};

export default EventsList;
